import React, { useEffect } from 'react';
import { Link } from 'react-router';
import {
  ArrowRightIcon,
  ScaleIcon,
  ClockIcon,
  UsersIcon,
  DocumentTextIcon,
  FingerPrintIcon,
  NoSymbolIcon,
  AdjustmentsHorizontalIcon,
  Cog6ToothIcon,
  EyeIcon,
  PhoneArrowDownLeftIcon,
  DocumentMagnifyingGlassIcon,
  ChartBarSquareIcon,
  BellAlertIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { PublicLayout } from '../components/layouts';
import { Seo } from '../seo/Seo';
import { HOME_FAQS } from '../seo/jsonLd';
import type { Locale } from '../seo/siteConfig';

const LandingPage: React.FC = () => {
  const { t, i18n } = useTranslation(['landing', 'common']);
  const locale: Locale = (i18n.language || 'es').startsWith('en') ? 'en' : 'es';

  // Scroll-in animation observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('is-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const regionalCountries = [
    { key: 'paraguay', accent: 'text-amber-400' },
    { key: 'mexico', accent: 'text-sky-400' },
    { key: 'colombia', accent: 'text-sky-400' },
    { key: 'brazil', accent: 'text-sky-400' },
  ] as const;

  const ootbDetections = [
    { key: 'sensitiveData', icon: FingerPrintIcon },
    { key: 'mandatoryDisclosures', icon: DocumentTextIcon },
  ] as const;

  const configurableDetections = [
    { key: 'unfoundedThreats', icon: ScaleIcon },
    { key: 'afterHours', icon: ClockIcon },
    { key: 'thirdParty', icon: UsersIcon },
    { key: 'scriptDeviations', icon: NoSymbolIcon },
  ] as const;

  const howItWorksSteps = [
    { key: 'ingestion', icon: PhoneArrowDownLeftIcon },
    { key: 'transcription', icon: DocumentMagnifyingGlassIcon },
    { key: 'evaluation', icon: ChartBarSquareIcon },
    { key: 'scoring', icon: BellAlertIcon },
  ] as const;

  const pilotSteps = ['evaluation', 'comparison', 'detection', 'measurement'] as const;

  const teamPillars = [
    { key: 'ownCriteria', icon: AdjustmentsHorizontalIcon },
    { key: 'calibration', icon: Cog6ToothIcon },
    { key: 'transparency', icon: EyeIcon },
  ] as const;

  return (
    <PublicLayout transparentHeader defersNavLogoToHeroScroll>
      <Seo route="/" includeSoftwareApplication faqs={HOME_FAQS[locale]} />

      {/* Hero — slide 1 */}
      <section className="relative bg-black overflow-hidden">
        {/* Subtle radial glow in upper-right, like the deck */}
        <div
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 80% 20%, rgba(14,165,233,0.10), transparent 60%)',
          }}
        />
        <div className="container-custom pt-40 pb-32 lg:pt-52 lg:pb-40 relative">
          <div className="max-w-5xl">
            {/* Hero-prominence logo: left-aligned with the h1, smaller than
                the headline (h1 renders at text-8xl ≈ 96px tall; this sits at
                h-24 = 96px layout box, with wordmark ~21px tall — readable
                hero-scale but visually subordinate to the headline). */}
            <img
              src="/logos/callcov-white.svg"
              alt="CallCov"
              className="h-24 w-auto mb-10 animate-fade-in-up"
            />
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light tracking-tight text-white leading-[1.05]">
              {t('landing:hero.title')}
            </h1>
            <div className="mt-8 h-px w-24 bg-white/30" />
            <p className="mt-8 text-lg sm:text-xl text-white/60 max-w-2xl leading-relaxed">
              {t('landing:hero.subtitle')}
            </p>
            <div className="mt-12 flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white text-base font-medium px-7 py-3.5 transition-colors group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              >
                {t('landing:hero.ctaPrimary')}
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black text-base font-medium px-2 py-3.5 transition-colors group"
              >
                {t('landing:hero.ctaSecondary')}
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
            <p className="mt-10 text-sm text-white/60">
              {t('landing:hero.announcement')}
            </p>
          </div>
        </div>
      </section>

      {/* Compliance disclaimer — subtle */}
      <section className="bg-black border-y border-white/5">
        <div className="container-custom py-3">
          <p className="text-xs text-white/50 text-center">
            {t('landing:complianceDisclaimer.text')}{' '}
            <Link to="/terms#section10" className="underline hover:text-white/80">
              {t('landing:complianceDisclaimer.learnMore')}
            </Link>
          </p>
        </div>
      </section>

      {/* Visibility gap — slide 2 */}
      <section className="bg-black">
        <div className="container-custom py-24 lg:py-32">
          <div className="max-w-3xl mb-16 animate-on-scroll">
            <p className="text-sm uppercase tracking-[0.2em] text-white/60 mb-4">
              {t('landing:visibilityGap.sectionBadge')}
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight">
              {t('landing:visibilityGap.sectionTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12 md:gap-0 animate-on-scroll">
            <div className="md:pr-12">
              <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-6">
                {t('landing:visibilityGap.today.caption')}
              </p>
              <div className="text-7xl sm:text-8xl font-light text-amber-400 mb-6 leading-none">
                {t('landing:visibilityGap.today.value')}
              </div>
              <p className="text-base text-white/60">
                {t('landing:visibilityGap.today.label')}
              </p>
            </div>
            <div className="md:pl-12 md:border-l md:border-white/10">
              <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-6">
                {t('landing:visibilityGap.withCallCov.caption')}
              </p>
              <div className="text-7xl sm:text-8xl font-light text-sky-400 mb-6 leading-none">
                {t('landing:visibilityGap.withCallCov.value')}
              </div>
              <p className="text-base text-white/60">
                {t('landing:visibilityGap.withCallCov.label')}
              </p>
            </div>
          </div>
          <p className="mt-16 text-sm text-white/60 max-w-2xl">
            {t('landing:visibilityGap.footnote')}
          </p>
        </div>
      </section>

      {/* Regional risk — slides 4-5 */}
      <section className="bg-black border-t border-white/5">
        <div className="container-custom py-24 lg:py-32">
          <div className="max-w-3xl mb-16 animate-on-scroll">
            <p className="text-sm uppercase tracking-[0.2em] text-white/60 mb-4">
              {t('landing:regionalRisk.sectionBadge')}
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight">
              {t('landing:regionalRisk.sectionTitle')}
            </h2>
            <p className="mt-6 text-lg text-white/50">
              {t('landing:regionalRisk.sectionSubtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-white/10">
            {regionalCountries.map(({ key, accent }, idx) => (
              <div key={key} className={`animate-on-scroll py-10 ${idx === 0 ? 'lg:pr-8' : 'lg:px-8'} ${idx === regionalCountries.length - 1 ? 'lg:pl-8 lg:pr-0' : ''}`}>
                <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-2">
                  {t(`landing:regionalRisk.countries.${key}.regulator`)}
                </p>
                <h3 className="text-2xl font-light text-white mb-8">
                  {t(`landing:regionalRisk.countries.${key}.name`)}
                </h3>
                <div className={`text-5xl lg:text-6xl font-light ${accent} mb-3 leading-none`}>
                  {t(`landing:regionalRisk.countries.${key}.stat1Value`)}
                </div>
                <p className="text-sm text-white/60 mb-8">
                  {t(`landing:regionalRisk.countries.${key}.stat1Label`)}
                </p>
                <div className={`text-3xl font-light ${accent} mb-2 leading-none`}>
                  {t(`landing:regionalRisk.countries.${key}.stat2Value`)}
                </div>
                <p className="text-sm text-white/60 mb-8">
                  {t(`landing:regionalRisk.countries.${key}.stat2Label`)}
                </p>
                <div className="border-t border-white/10 pt-6">
                  <p className="text-xs uppercase tracking-[0.15em] text-sky-400 mb-2">
                    {t(`landing:regionalRisk.countries.${key}.highlight`)}
                  </p>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {t(`landing:regionalRisk.countries.${key}.context`)}
                  </p>
                </div>
                {idx < pilotSteps.length - 1 && (
                  <ArrowRightIcon className="hidden lg:block absolute top-1/2 -right-4 w-6 h-6 text-primary-300 -translate-y-1/2" aria-hidden="true" />
                )}
              </div>
            ))}
          </div>

          <p className="text-center text-sm text-neutral-500 italic">
            {t('landing:pilotPlan.pricingNote')}
          </p>
        </div>
      </section>

      {/* Detections catalog — slide 3 */}
      <section className="bg-black border-t border-white/5">
        <div className="container-custom py-24 lg:py-32">
          <div className="max-w-3xl mb-16 animate-on-scroll">
            <p className="text-sm uppercase tracking-[0.2em] text-white/60 mb-4">
              {t('landing:detectionsCatalog.sectionBadge')}
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight">
              {t('landing:detectionsCatalog.sectionTitle')}
            </h2>
            <p className="mt-6 text-lg text-white/50">
              {t('landing:detectionsCatalog.sectionSubtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 lg:divide-x lg:divide-white/10">
            {/* Out of the box */}
            <div className="py-10 lg:pr-12 animate-on-scroll">
              <p className="text-xs uppercase tracking-[0.2em] text-sky-400 mb-2">
                {t('landing:detectionsCatalog.outOfTheBox.title')}
              </p>
              <p className="text-sm text-white/60 mb-10">
                {t('landing:detectionsCatalog.outOfTheBox.subtitle')}
              </p>
              <div className="space-y-10">
                {ootbDetections.map(({ key, icon: Icon }) => (
                  <div key={key} className="flex gap-4">
                    <Icon className="w-6 h-6 text-sky-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-base font-medium text-white mb-2">
                        {t(`landing:detectionsCatalog.outOfTheBox.items.${key}.title`)}
                      </h4>
                      <p className="text-sm text-white/50 leading-relaxed">
                        {t(`landing:detectionsCatalog.outOfTheBox.items.${key}.description`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Configurable */}
            <div className="py-10 lg:pl-12 animate-on-scroll">
              <p className="text-xs uppercase tracking-[0.2em] text-amber-400 mb-2">
                {t('landing:detectionsCatalog.configurable.title')}
              </p>
              <p className="text-sm text-white/60 mb-10">
                {t('landing:detectionsCatalog.configurable.subtitle')}
              </p>
              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-10">
                {configurableDetections.map(({ key, icon: Icon }) => (
                  <div key={key} className="flex gap-4">
                    <Icon className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="text-base font-medium text-white mb-2">
                        {t(`landing:detectionsCatalog.configurable.items.${key}.title`)}
                      </h4>
                      <p className="text-sm text-white/50 leading-relaxed">
                        {t(`landing:detectionsCatalog.configurable.items.${key}.description`)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-12 text-xs text-white/60 italic">
            {t('landing:detectionsCatalog.footnote')}
          </p>
        </div>
      </section>

      {/* How it works — slide 6 */}
      <section className="bg-black border-t border-white/5">
        <div className="container-custom py-24 lg:py-32">
          <div className="max-w-3xl mb-20 animate-on-scroll">
            <p className="text-sm uppercase tracking-[0.2em] text-white/60 mb-4">
              {t('landing:howItWorks.sectionBadge')}
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight">
              {t('landing:howItWorks.sectionTitle')}
            </h2>
            <p className="mt-6 text-lg text-white/50">
              {t('landing:howItWorks.sectionSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8 relative">
            {howItWorksSteps.map(({ key, icon: Icon }, idx) => (
              <div key={key} className="relative animate-on-scroll">
                <div className="text-5xl font-light text-sky-400 mb-4 leading-none">
                  {t(`landing:howItWorks.steps.${key}.number`)}
                </div>
                <Icon className="w-5 h-5 text-white/60 mb-4" />
                <h3 className="text-lg font-medium text-white mb-3">
                  {t(`landing:howItWorks.steps.${key}.title`)}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed">
                  {t(`landing:howItWorks.steps.${key}.description`)}
                </p>
                {idx < howItWorksSteps.length - 1 && (
                  <ArrowRightIcon
                    className="hidden lg:block absolute top-3 -right-6 w-5 h-5 text-white/20"
                    aria-hidden="true"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team criteria — slide 7 */}
      <section className="bg-black border-t border-white/5">
        <div className="container-custom py-24 lg:py-32">
          <div className="max-w-5xl mb-16 animate-on-scroll">
            <p className="text-sm uppercase tracking-[0.2em] text-white/60 mb-4">
              {t('landing:teamCriteria.sectionBadge')}
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light leading-tight">
              <span className="text-white">{t('landing:teamCriteria.sectionTitleLine1')}</span>
              <br />
              <span className="text-sky-400">{t('landing:teamCriteria.sectionTitleLine2')}</span>
            </h2>
            <p className="mt-6 text-lg text-white/50">
              {t('landing:teamCriteria.sectionSubtitle')}
            </p>
          </div>

          <div className="border-t border-white/10 pt-16">
            <div className="grid md:grid-cols-3 gap-x-12 gap-y-10">
              {teamPillars.map(({ key, icon: Icon }) => (
                <div key={key} className="animate-on-scroll">
                  <Icon className="w-6 h-6 text-sky-400 mb-4" />
                  <h3 className="text-lg font-medium text-white mb-3">
                    {t(`landing:teamCriteria.pillars.${key}.title`)}
                  </h3>
                  <p className="text-sm text-white/50 leading-relaxed">
                    {t(`landing:teamCriteria.pillars.${key}.description`)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use cases — slide 8 */}
      <section className="bg-black border-t border-white/5">
        <div className="container-custom py-24 lg:py-32">
          <div className="max-w-3xl mb-16 animate-on-scroll">
            <p className="text-sm uppercase tracking-[0.2em] text-white/60 mb-4">
              {t('landing:useCases.sectionBadge')}
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight">
              {t('landing:useCases.sectionTitle')}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-x-12 gap-y-16">
            <div className="animate-on-scroll">
              <p className="text-xs uppercase tracking-[0.2em] text-sky-400 mb-3">
                {t('landing:useCases.list.customerService.outcome')}
              </p>
              <h3 className="text-2xl font-light text-white mb-4">
                {t('landing:useCases.list.customerService.title')}
              </h3>
              <p className="text-white/50 mb-8 leading-relaxed">
                {t('landing:useCases.list.customerService.description')}
              </p>
              <ul className="space-y-3">
                {(['scoring', 'training', 'alerts', 'compliance'] as const).map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-white/70">
                    <span className="text-sky-400 mt-1">—</span>
                    <span>{t(`landing:useCases.list.customerService.benefits.${b}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="animate-on-scroll md:border-l md:border-white/10 md:pl-12">
              <p className="text-xs uppercase tracking-[0.2em] text-sky-400 mb-3">
                {t('landing:useCases.list.qa.outcome')}
              </p>
              <h3 className="text-2xl font-light text-white mb-4">
                {t('landing:useCases.list.qa.title')}
              </h3>
              <p className="text-white/50 mb-8 leading-relaxed">
                {t('landing:useCases.list.qa.description')}
              </p>
              <ul className="space-y-3">
                {(['scoring', 'compliance', 'dashboards', 'trends'] as const).map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm text-white/70">
                    <span className="text-sky-400 mt-1">—</span>
                    <span>{t(`landing:useCases.list.qa.benefits.${b}`)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pilot plan — slide 9 */}
      <section className="bg-black border-t border-white/5">
        <div className="container-custom py-24 lg:py-32">
          <div className="max-w-4xl mb-20 animate-on-scroll">
            <p className="text-sm uppercase tracking-[0.2em] text-white/60 mb-4">
              {t('landing:pilotPlan.sectionBadge')}
            </p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight">
              {t('landing:pilotPlan.sectionTitle')}
            </h2>
            <p className="mt-6 text-lg text-white/50">
              {t('landing:pilotPlan.sectionSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8 mb-12">
            {pilotSteps.map((stepKey, idx) => (
              <div key={stepKey} className="relative animate-on-scroll">
                <div className="text-5xl font-light text-sky-400 mb-4 leading-none">
                  {t(`landing:pilotPlan.steps.${stepKey}.number`)}
                </div>
                <h3 className="text-lg font-medium text-white mb-3">
                  {t(`landing:pilotPlan.steps.${stepKey}.title`)}
                </h3>
                <p className="text-sm text-white/50 leading-relaxed">
                  {t(`landing:pilotPlan.steps.${stepKey}.description`)}
                </p>
                {idx < pilotSteps.length - 1 && (
                  <ArrowRightIcon
                    className="hidden lg:block absolute top-3 -right-6 w-5 h-5 text-white/20"
                    aria-hidden="true"
                  />
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 pt-8">
            <p className="text-sm text-white/60 italic">
              {t('landing:pilotPlan.pricingNote')}
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA — slide 10 */}
      <section className="bg-black border-t border-white/5">
        <div className="container-custom py-32 lg:py-40">
          <div className="max-w-3xl mx-auto text-center">
            <img
              src="/logos/callcov-white.svg"
              alt="CallCov"
              className="h-16 w-auto mx-auto mb-12 animate-on-scroll"
            />
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light text-white leading-tight animate-on-scroll">
              {t('landing:finalCta.title')}
            </h2>
            <p className="mt-8 text-lg text-white/60 animate-on-scroll">
              {t('landing:finalCta.subtitle')}
            </p>
            <div className="mt-16 mx-auto max-w-md animate-on-scroll">
              <div className="border-t border-white/10 pt-10 space-y-6">
                <a
                  href="mailto:ventas@callcov.com"
                  className="inline-flex items-center gap-3 text-base text-white/80 hover:text-white transition-colors"
                >
                  <EnvelopeIcon className="w-5 h-5" />
                  ventas@callcov.com
                </a>
                <div>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-sky-400 hover:text-sky-300 focus-visible:text-sky-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-black text-base font-medium transition-colors group"
                  >
                    {t('landing:finalCta.ctaPrimary')}
                    <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default LandingPage;
